import { Box, Button, Modal } from '@mui/material';

import { useQuery } from 'react-query';

import * as api from '../../api/app/dashboardsOpcButtonApi';

import { DashboardOpcButton, DashboardOpcButtonInputs } from '../../types/app/dashboardOpcButton';

import { ApiError } from '../../components/core/ApiError/ApiError';

import { Loader } from '../../components/core/Loader/Loader';

import { useEffect, useState } from 'react';

import { DashboardOpcSetting } from '../../types/app/dashboardOpcSetting';

import { ThemeType, useLocalTheme } from '../../hooks/useLocalTheme';

import { DashboardOpcButtonForm } from './DashboardOpcButtonForm';

interface Props {
  dataOpcSetting: DashboardOpcSetting;
  assetId: number;
}

export const DashboardOpcGrid = ({ dataOpcSetting, assetId }: Props) => {
  const [showAddButtonModal, setShowAddButtonModal] = useState<{
    defaultValues: DashboardOpcButtonInputs;
    isEdit: boolean;
  } | null>(null);
  const [isDarkTheme, setIsDarkTheme] = useState<boolean>(false);
  const { localTheme } = useLocalTheme();

  const {
    data: opcButtons,
    isLoading,
    isError,
    refetch,
  } = useQuery<DashboardOpcButton[]>(['dashboardOpcButtons', dataOpcSetting.dashboardId], () =>
    api.getDashboardOpcButtonList(Number(dataOpcSetting.dashboardId)),
  );

  const cells = Array.from({ length: dataOpcSetting.rows * dataOpcSetting.columns });

  useEffect(() => {
    setIsDarkTheme(localTheme === ThemeType.DARK);
  }, [localTheme]);

  if (isError) return <ApiError />;
  if (isLoading) return <Loader />;
  return (
    <>
      <Box sx={{ overflowX: 'auto' }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: `repeat(${dataOpcSetting.columns}, minmax(100px, 1fr))`,
            gap: 1,
            minWidth: dataOpcSetting.columns * 100, // aby se spustil horizontální scroll na menších obrazovkách
          }}
        >
          {cells.map((_, index) => {
            const row = Math.floor(index / dataOpcSetting.columns);
            const col = index % dataOpcSetting.columns;

            const opcButton = opcButtons?.find((x) => x.column === col && x.row === row);

            return (
              <Box
                key={index}
                sx={{
                  borderRadius: 1,
                  padding: 2,
                  minHeight: 60,
                  backgroundColor: isDarkTheme ? '#413f3f' : 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: isDarkTheme ? undefined : 'solid 1px #E98300',
                }}
              >
                {opcButton ? (
                  <Button
                    type="button"
                    variant="contained"
                    color={opcButton.style}
                    onClick={() =>
                      setShowAddButtonModal({
                        defaultValues: {
                          ...opcButton,
                        },
                        isEdit: true,
                      })
                    }
                    style={{
                      color: 'white',
                      fontSize: opcButton.fontSize ? `${opcButton.fontSize}px` : '15px',
                    }}
                  >
                    {opcButton.name}
                  </Button>
                ) : (
                  <Button
                    type="button"
                    variant="contained"
                    color="secondary"
                    onClick={() =>
                      setShowAddButtonModal({
                        defaultValues: {
                          dashboardId: dataOpcSetting.dashboardId,
                          row: row,
                          column: col,
                        },
                        isEdit: false,
                      })
                    }
                  >
                    Add button
                  </Button>
                )}
              </Box>
            );
          })}
        </Box>
      </Box>
      {showAddButtonModal && (
        <Modal
          open={!!showAddButtonModal}
          onClose={() => setShowAddButtonModal(null)}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
        >
          <DashboardOpcButtonForm
            defaultValues={{
              ...showAddButtonModal.defaultValues,
              fontSize: showAddButtonModal.defaultValues.fontSize || '15',
            }}
            isEdit={showAddButtonModal.isEdit}
            assetId={assetId}
            onSuccess={() => {
              setShowAddButtonModal(null);
              refetch();
            }}
          />
        </Modal>
      )}
    </>
  );
};
